import { useRef, useState, useEffect } from 'react';
import SignaturePad from 'signature_pad';
import './SignatureModal.css';

export default function SignatureModal({ onConfirm, onClose, savedSignature, onDeletePreset }) {
  const hasSaved = !!savedSignature;
  const [tab, setTab]             = useState(hasSaved ? 'saved' : 'draw');
  const [typedName, setTypedName] = useState('');
  const [uploadedImg, setUploadedImg] = useState(null);
  const [saveAsPreset, setSaveAsPreset] = useState(false);
  const canvasRef = useRef(null);
  const padRef    = useRef(null);

  // Spin up signature_pad whenever the Draw tab becomes active.
  // Re-sizes the canvas to its CSS box × devicePixelRatio for crisp output on retina/mobile.
  useEffect(() => {
    if (tab !== 'draw' || !canvasRef.current) return;
    const canvas = canvasRef.current;

    const resize = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const data  = padRef.current?.toData();
      canvas.width  = canvas.offsetWidth  * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d').scale(ratio, ratio);
      padRef.current?.clear();
      if (data?.length) padRef.current.fromData(data);
    };

    const pad = new SignaturePad(canvas, {
      minWidth: 0.6,
      maxWidth: 2.4,
      penColor: '#111',
      backgroundColor: 'rgba(0,0,0,0)',
    });
    padRef.current = pad;
    resize();

    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      pad.off();
      padRef.current = null;
    };
  }, [tab]);

  const clearCanvas = () => padRef.current?.clear();

  const confirm = () => {
    let base64 = null;
    if (tab === 'saved') {
      base64 = savedSignature;
    } else if (tab === 'draw') {
      if (!padRef.current || padRef.current.isEmpty()) return;
      base64 = padRef.current.toDataURL('image/png');
    } else if (tab === 'type') {
      if (!typedName.trim()) return;
      const c = document.createElement('canvas');
      c.width = 400; c.height = 100;
      const ctx = c.getContext('2d');
      ctx.font = '52px Georgia, serif';
      ctx.fillStyle = '#111';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedName, 20, 50);
      base64 = c.toDataURL('image/png');
    } else {
      if (!uploadedImg) return;
      base64 = uploadedImg;
    }
    onConfirm(base64, saveAsPreset && tab !== 'saved');
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setUploadedImg(ev.target.result);
    reader.readAsDataURL(file);
  };

  const tabs = hasSaved
    ? ['saved', 'draw', 'type', 'upload']
    : ['draw', 'type', 'upload'];

  const tabLabel = { saved: 'Opgeslagen', draw: 'Tekenen', type: 'Typen', upload: 'Uploaden' };

  return (
    <div className="sig-modal-overlay" onClick={onClose}>
      <div className="sig-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Handtekening Toevoegen</h3>
        <div className="sig-tabs">
          {tabs.map((t) => (
            <button key={t} className={`sig-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
              {tabLabel[t]}
            </button>
          ))}
        </div>

        {tab === 'saved' && (
          <div className="sig-saved-wrap">
            <img src={savedSignature} alt="opgeslagen handtekening" className="sig-saved-img" />
            <button className="sig-btn delete" onClick={onDeletePreset}>Opgeslagen verwijderen</button>
          </div>
        )}

        {tab === 'draw' && (
          <>
            <div className="sig-canvas-wrap">
              <canvas ref={canvasRef} className="sig-canvas" />
            </div>
            <div className="sig-actions">
              <button className="sig-btn clear" onClick={clearCanvas}>Wissen</button>
            </div>
          </>
        )}

        {tab === 'type' && (
          <input
            className="sig-type-input"
            placeholder="Typ je naam"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
          />
        )}

        {tab === 'upload' && (
          <label className="sig-upload-area">
            {uploadedImg
              ? <img src={uploadedImg} alt="voorbeeld handtekening" />
              : 'Klik om een afbeelding van een handtekening te uploaden'}
            <input type="file" accept="image/*" hidden onChange={handleUpload} />
          </label>
        )}

        {tab !== 'saved' && (
          <label className="sig-save-preset">
            <input
              type="checkbox"
              checked={saveAsPreset}
              onChange={(e) => setSaveAsPreset(e.target.checked)}
            />
            Opslaan als mijn handtekening
          </label>
        )}

        <div className="sig-actions" style={{ marginTop: 16 }}>
          <button className="sig-btn secondary" onClick={onClose}>Annuleren</button>
          <button className="sig-btn primary" onClick={confirm}>Handtekening Gebruiken</button>
        </div>
      </div>
    </div>
  );
}
